/**
 * Reemplaza el AuthProvider original (Supabase Auth + RPC auth_policy/my_authz)
 * por uno que habla con el Worker propio. La forma publica (MyAuthz, session,
 * mfa, mfaSatisfied) se mantiene identica a proposito -- es lo que permite que
 * AppShell, guards.tsx y las paginas reales no necesiten cambios.
 *
 * MFA -- ya NO es un stub. El backend lo tiene completo (src/mfa.ts) detras
 * de un flag apagado por defecto (`app_settings['auth.require_mfa']`); esta
 * pasada conecta el flujo real: cuando el login devuelve
 * mfa_enroll_required/mfa_challenge_required (nunca pasa hoy con el flag
 * apagado, pero si se prende), no hay sesion real todavia -- solo un
 * "pending token" de 5 minutos que sirve unicamente para /mfa/enroll* y
 * /mfa/challenge (ver src/auth.ts en el backend). Se guarda una sesion
 * PARCIAL (`session` con el email, `mfaSatisfied=false`) para que
 * RequireSession no rebote a /login -- eso es lo que deja que guards.tsx,
 * sin tocar, redirija a /mfa/inscribir o /mfa/verificar.
 *
 * Permisos (Fase 5): ya NO se inventan en el cliente. `GET /me/access` entrega
 * los roles y, por permiso, donde aplica ('ALL' o las unidades); ver
 * auth/access.ts. `authz` (vocabulario viejo de @rat/shared) se deriva de ahi
 * para las paginas que solo preguntan "puede X en alguna parte", y `access`
 * se expone para los controles ligados a una unidad (`canIn`).
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { EMPTY_AUTHZ, type MyAuthz } from '@rat/shared';
import { apiFetch, clearToken, getToken, login as backendLogin, setToken } from '../lib/backend';
import { deriveMyAuthz, EMPTY_ACCESS, type AccessResponse } from './access';

export interface Session {
  user: { email: string };
}

type MfaState = 'unknown' | 'not_required' | 'not_enrolled' | 'needs_challenge' | 'verified';

interface MeResponse {
  userId: string;
  email: string;
  isInstitutional: boolean;
  isAuditor: boolean;
  units: Array<{ unit_id: string; name: string; unit_role: 'jefe' | 'colaborador' | 'consulta' }>;
}

interface AuthContextValue {
  loading: boolean;
  session: Session | null;
  mfa: MfaState;
  mfaSatisfied: boolean;
  /** Solo mientras mfa es 'not_enrolled'/'needs_challenge' -- lo consumen MfaEnroll.tsx/MfaChallenge.tsx. */
  pendingMfaToken: string | null;
  authz: MyAuthz;
  /** Permisos con alcance (por unidad) tal como los entrega GET /me/access: usar `canIn` para controles de una unidad. */
  access: AccessResponse;
  refreshAuthz: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  /** Llamado por MfaEnroll.tsx/MfaChallenge.tsx al terminar el segundo factor -- entrega la sesion real. */
  completeMfa: (sessionToken: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [authz, setAuthz] = useState<MyAuthz>(EMPTY_AUTHZ);
  const [access, setAccess] = useState<AccessResponse>(EMPTY_ACCESS);
  const [mfa, setMfa] = useState<MfaState>('not_required');
  const [pendingMfaToken, setPendingMfaToken] = useState<string | null>(null);

  async function hydrate(): Promise<void> {
    if (!getToken()) {
      setSession(null);
      setAuthz(EMPTY_AUTHZ);
      setAccess(EMPTY_ACCESS);
      setLoading(false);
      return;
    }
    try {
      // En paralelo: /me (identidad y unidades asignadas) y /me/access (roles y permisos con alcance).
      const [me, acc] = await Promise.all([apiFetch<MeResponse>('/me'), apiFetch<AccessResponse>('/me/access')]);
      setSession({ user: { email: me.email } });
      setAccess(acc);
      setAuthz(deriveMyAuthz(me, acc));
      setMfa('not_required'); // llegar aca via un token real ya prueba que el segundo factor (si aplicaba) esta resuelto
      setPendingMfaToken(null);
    } catch {
      clearToken();
      setSession(null);
      setAuthz(EMPTY_AUTHZ);
      setAccess(EMPTY_ACCESS);
    }
    setLoading(false);
  }

  useEffect(() => {
    void hydrate();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      loading,
      session,
      mfa,
      mfaSatisfied: mfa === 'not_required' || mfa === 'verified',
      pendingMfaToken,
      authz,
      access,
      refreshAuthz: hydrate,
      signIn: async (email: string, password: string) => {
        const outcome = await backendLogin(email, password);
        if (outcome.status === 'ok') {
          await hydrate();
          return;
        }
        // Sin sesion real todavia -- una "parcial" (solo el email) es
        // suficiente para que RequireSession no rebote a /login; guards.tsx
        // hace el resto redirigiendo a /mfa/inscribir o /mfa/verificar.
        setSession({ user: { email } });
        setPendingMfaToken(outcome.pendingToken);
        setMfa(outcome.status === 'mfa_enroll_required' ? 'not_enrolled' : 'needs_challenge');
      },
      completeMfa: async (sessionToken: string) => {
        setToken(sessionToken);
        setMfa('verified');
        setPendingMfaToken(null);
        await hydrate();
      },
      signOut: async () => {
        clearToken();
        // Recarga completa a /login SIN `from`: la siguiente cuenta cae en su inicio por rol (landingRouteFor),
        // no en la pagina donde estaba la anterior, y se descarta la cache de React Query de la cuenta saliente.
        window.location.replace('/login');
      },
    }),
    [loading, session, authz, access, mfa, pendingMfaToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuera de <AuthProvider>');
  return ctx;
}
